import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2, SlidersHorizontal, Palette, Pencil, Building2, Store, History, CreditCard, ChevronLeft } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn, formatDateTime } from '@/lib/utils';
import {
  adminApi,
  plansApi,
  type TenantSummary,
  type TenantInput,
  type TenantUsage,
} from '@/api/endpoints/admin';
import type { Branding } from '@/api/endpoints/me';
import { Modal } from './Modal';
import { ModuleEditor } from './ModuleEditor';
import { GatewayModal } from '@/features/billing/GatewayModal';

const STATUS_CLS: Record<string, string> = {
  ativo: 'bg-status-running/10 text-status-running',
  suspenso: 'bg-status-off/15 text-status-off',
  inadimplente: 'bg-status-blocked/10 text-status-blocked',
};

export function TenantsPage() {
  const qc = useQueryClient();
  const tenantsQ = useQuery({ queryKey: ['admin', 'tenants'], queryFn: () => adminApi.listTenants() });
  const usageQ = useQuery({ queryKey: ['admin', 'tenants', 'usage'], queryFn: () => adminApi.tenantsUsage(), retry: false });
  const usageById = new Map<number, TenantUsage>((usageQ.data ?? []).map((u) => [u.tenant_id, u]));
  const tenants = tenantsQ.data ?? [];
  const revendas = tenants.filter((t) => t.tipo === 'revenda');
  const clientes = tenants.filter((t) => t.tipo === 'cliente');

  const [editing, setEditing] = useState<TenantSummary | 'new' | null>(null);
  const [modulesOf, setModulesOf] = useState<TenantSummary | null>(null);
  const [brandingOf, setBrandingOf] = useState<TenantSummary | null>(null);
  const [auditOf, setAuditOf] = useState<TenantSummary | null>(null);
  const [gatewayOf, setGatewayOf] = useState<TenantSummary | null>(null);

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['admin', 'tenants'] });
  };

  // Editor em TELA cheia (padronizado com /settings/clients).
  if (editing) {
    return (
      <TenantEditor
        tenant={editing === 'new' ? null : editing}
        revendas={revendas}
        onClose={() => setEditing(null)}
        onSaved={() => { setEditing(null); refresh(); }}
      />
    );
  }

  return (
    <div className="space-y-5 p-4 sm:p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold">Revendas</h1>
          <p className="text-sm text-muted-foreground">Gestão de revendas: módulos/cotas, limites, white-label, planos e gateway.</p>
        </div>
        <Button onClick={() => setEditing('new')} className="gap-1.5"><Plus className="h-4 w-4" /> Novo tenant</Button>
      </div>

      {tenantsQ.isLoading ? (
        <p className="text-sm text-muted-foreground">Carregando…</p>
      ) : tenantsQ.error ? (
        <p className="text-sm text-destructive">Acesso restrito ao administrador.</p>
      ) : tenants.length === 0 ? (
        <Card><CardContent className="p-8 text-center text-sm text-muted-foreground">Nenhum tenant cadastrado. Crie uma revenda para começar.</CardContent></Card>
      ) : (
        <>
          <Section icon={Store} title="Revendas" tenants={revendas} usageById={usageById} onModules={setModulesOf} onBranding={setBrandingOf} onEdit={setEditing} onAudit={setAuditOf} onGateway={setGatewayOf} onChanged={refresh} />
          <Section icon={Building2} title="Clientes" tenants={clientes} usageById={usageById} onModules={setModulesOf} onBranding={setBrandingOf} onEdit={setEditing} onAudit={setAuditOf} onGateway={setGatewayOf} onChanged={refresh} />
        </>
      )}

      {modulesOf && <ModulesModal tenant={modulesOf} onClose={() => setModulesOf(null)} onSaved={refresh} />}
      {brandingOf && <BrandingModal tenant={brandingOf} onClose={() => setBrandingOf(null)} />}
      {auditOf && <AuditModal tenant={auditOf} onClose={() => setAuditOf(null)} />}
      {gatewayOf && <GatewayModal tenantId={gatewayOf.id} label={gatewayOf.nome} onClose={() => setGatewayOf(null)} />}
    </div>
  );
}

function Section({
  icon: Icon, title, tenants, usageById, onModules, onBranding, onEdit, onAudit, onGateway, onChanged,
}: {
  icon: typeof Store; title: string; tenants: TenantSummary[]; usageById: Map<number, TenantUsage>;
  onModules: (t: TenantSummary) => void; onBranding: (t: TenantSummary) => void;
  onEdit: (t: TenantSummary) => void; onAudit: (t: TenantSummary) => void;
  onGateway: (t: TenantSummary) => void; onChanged: () => void;
}) {
  if (tenants.length === 0) return null;
  return (
    <section className="space-y-2">
      <h2 className="flex items-center gap-2 text-sm font-bold"><Icon className="h-4 w-4 text-brand" /> {title} ({tenants.length})</h2>
      <div className="space-y-2">
        {tenants.map((t) => (
          <TenantRow key={t.id} t={t} usage={usageById.get(t.id)} onModules={onModules} onBranding={onBranding} onEdit={onEdit} onAudit={onAudit} onGateway={onGateway} onChanged={onChanged} />
        ))}
      </div>
    </section>
  );
}

function TenantRow({
  t, usage, onModules, onBranding, onEdit, onAudit, onGateway, onChanged,
}: {
  t: TenantSummary; usage?: TenantUsage; onModules: (t: TenantSummary) => void; onBranding: (t: TenantSummary) => void;
  onEdit: (t: TenantSummary) => void; onAudit: (t: TenantSummary) => void;
  onGateway: (t: TenantSummary) => void; onChanged: () => void;
}) {
  const del = useMutation({
    mutationFn: () => adminApi.deleteTenant(t.id),
    onSuccess: onChanged,
  });
  // limite <= 0 = ilimitado (∞). Mostra uso/limite quando o uso já carregou.
  const lim = (v: number) => (v > 0 ? String(v) : '∞');
  const dev = usage ? `${usage.device_count}/${lim(t.device_limit)}` : lim(t.device_limit);
  const usr = usage ? `${usage.user_count}/${lim(t.user_limit)}` : lim(t.user_limit);
  const near = (used: number, limit: number) => limit > 0 && used >= limit;
  const overDev = usage ? near(usage.device_count, t.device_limit) : false;
  const overUsr = usage ? near(usage.user_count, t.user_limit) : false;
  return (
    <Card>
      <CardContent className="flex flex-wrap items-center justify-between gap-3 p-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <p className="truncate text-sm font-bold">{t.nome_exibicao || t.nome}</p>
            <span className={cn('rounded-full px-2 py-0.5 text-[10px] font-bold', STATUS_CLS[t.status] ?? 'bg-secondary')}>{t.status}</span>
          </div>
          <p className="text-[11px] text-muted-foreground">
            {t.plano ? `${t.plano} · ` : ''}{t.modulos_on} módulos · <span className={cn(overDev && 'font-bold text-status-blocked')}>{dev} disp.</span> · <span className={cn(overUsr && 'font-bold text-status-blocked')}>{usr} usuários</span>
            {t.subdominio ? ` · ${t.subdominio}` : ''}
          </p>
        </div>
        <div className="flex items-center gap-1">
          <Button variant="outline" size="sm" className="gap-1.5" onClick={() => onModules(t)}><SlidersHorizontal className="h-3.5 w-3.5" /> Módulos</Button>
          <Button variant="outline" size="sm" className="gap-1.5" onClick={() => onBranding(t)}><Palette className="h-3.5 w-3.5" /> White-label</Button>
          {t.tipo === 'revenda' && (
            <Button variant="outline" size="sm" className="gap-1.5" onClick={() => onGateway(t)}><CreditCard className="h-3.5 w-3.5" /> Gateway</Button>
          )}
          <button type="button" onClick={() => onAudit(t)} className="rounded-md p-1.5 text-muted-foreground hover:bg-accent" aria-label="Histórico" title="Histórico de alterações"><History className="h-4 w-4" /></button>
          <button type="button" onClick={() => onEdit(t)} className="rounded-md p-1.5 text-muted-foreground hover:bg-accent" aria-label="Editar"><Pencil className="h-4 w-4" /></button>
          <button type="button" onClick={() => { if (window.confirm(`Excluir o tenant "${t.nome}"? Módulos e branding serão removidos.`)) del.mutate(); }} className="rounded-md p-1.5 text-muted-foreground hover:bg-accent" aria-label="Excluir"><Trash2 className="h-4 w-4" /></button>
        </div>
      </CardContent>
    </Card>
  );
}

function TenantEditor({
  tenant, revendas, onClose, onSaved,
}: {
  tenant: TenantSummary | null; revendas: TenantSummary[]; onClose: () => void; onSaved: () => void;
}) {
  const isEdit = !!tenant;
  const usersQ = useQuery({ queryKey: ['admin', 'traccar-users'], queryFn: () => adminApi.traccarUsers() });
  const [tipo, setTipo] = useState(tenant?.tipo ?? 'revenda');
  const [nome, setNome] = useState(tenant?.nome ?? '');
  const [traccarUserId, setTraccarUserId] = useState<number | ''>(tenant?.traccar_user_id ?? '');
  const [parentId, setParentId] = useState<number | ''>(tenant?.parent_id ?? '');
  const [plano, setPlano] = useState(tenant?.plano ?? '');
  const [deviceLimit, setDeviceLimit] = useState(String(tenant?.device_limit ?? 0));
  const [userLimit, setUserLimit] = useState(String(tenant?.user_limit ?? 0));
  const [statusV, setStatusV] = useState(tenant?.status ?? 'ativo');
  // Ficha cadastral (cliente/revenda) — seções endereço, dados, financeiro
  const [cad, setCad] = useState({
    endereco: tenant?.endereco ?? '', cidade: tenant?.cidade ?? '', uf: tenant?.uf ?? '',
    pais: tenant?.pais ?? '', cep: tenant?.cep ?? '',
    cod_interno: tenant?.cod_interno ?? '', documento: tenant?.documento ?? '',
    cpf_cnpj: tenant?.cpf_cnpj ?? '', email: tenant?.email ?? '',
    telefone: tenant?.telefone ?? '', telefone_urgencia: tenant?.telefone_urgencia ?? '',
    responsavel: tenant?.responsavel ?? '',
    valor_plano: tenant?.valor_plano ?? '', tipo_cobranca: tenant?.tipo_cobranca ?? '',
    data_vencimento: tenant?.data_vencimento ?? '', obs_financeira: tenant?.obs_financeira ?? '',
    obs: tenant?.obs ?? '',
  });
  const setC = (k: keyof typeof cad, v: string) => setCad((c) => ({ ...c, [k]: v }));

  const save = useMutation({
    mutationFn: () => {
      const t = (v: string) => v.trim() || null;
      const cadOut = Object.fromEntries(Object.entries(cad).map(([k, v]) => [k, t(v)]));
      const body: TenantInput = {
        tipo, nome: nome.trim(),
        traccar_user_id: traccarUserId === '' ? null : Number(traccarUserId),
        parent_id: tipo === 'cliente' && parentId !== '' ? Number(parentId) : null,
        plano: plano.trim() || null,
        device_limit: Number(deviceLimit) || 0,
        user_limit: Number(userLimit) || 0,
        status: statusV,
        ...cadOut,
      };
      return isEdit ? adminApi.patchTenant(tenant!.id, body) : adminApi.createTenant(body);
    },
    onSuccess: onSaved,
  });

  const valid = nome.trim().length > 0;

  return (
    <div className="mx-auto max-w-3xl space-y-4 p-4 sm:p-6">
      <button
        type="button"
        onClick={onClose}
        className="-ml-2 inline-flex items-center gap-0.5 rounded-md px-2 py-1 text-sm font-medium text-primary hover:bg-primary/5"
      >
        <ChevronLeft className="h-5 w-5" />
        Voltar
      </button>
      <div>
        <h1 className="text-xl font-bold">{isEdit ? `Editar ${tenant!.nome}` : 'Novo tenant'}</h1>
        <p className="text-sm text-muted-foreground">Cadastro de {tipo === 'cliente' ? 'cliente' : 'revenda'}: dados, limites, módulos e financeiro.</p>
      </div>
      <Card>
        <CardContent className="space-y-3 p-4">
        {save.error && <p className="text-xs text-destructive">{(save.error as Error).message}</p>}
        <Field label="Tipo">
          <select value={tipo} onChange={(e) => setTipo(e.target.value)} disabled={isEdit} className="h-10 w-full rounded-md border border-input bg-background px-2 text-sm">
            <option value="revenda">Revenda</option>
            <option value="cliente">Cliente</option>
          </select>
        </Field>
        <Field label="Nome"><Input value={nome} onChange={(e) => setNome(e.target.value)} placeholder="Nome do tenant" /></Field>
        {tipo === 'cliente' && (
          <Field label="Revenda (pai)">
            <select value={parentId} onChange={(e) => setParentId(e.target.value ? Number(e.target.value) : '')} className="h-10 w-full rounded-md border border-input bg-background px-2 text-sm">
              <option value="">— (cliente direto)</option>
              {revendas.map((r) => <option key={r.id} value={r.id}>{r.nome}</option>)}
            </select>
          </Field>
        )}
        <Field label="Usuário Traccar vinculado">
          <select value={traccarUserId} onChange={(e) => setTraccarUserId(e.target.value ? Number(e.target.value) : '')} className="h-10 w-full rounded-md border border-input bg-background px-2 text-sm">
            <option value="">— (vincular depois)</option>
            {(usersQ.data ?? []).map((u) => (
              <option key={u.id} value={u.id}>{u.name}{u.email ? ` · ${u.email}` : ''}{u.administrator ? ' (admin)' : u.userLimit ? ' (manager)' : ''}</option>
            ))}
          </select>
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Plano"><Input value={plano} onChange={(e) => setPlano(e.target.value)} placeholder="(opcional)" /></Field>
          <Field label="Status">
            <select value={statusV} onChange={(e) => setStatusV(e.target.value)} className="h-10 w-full rounded-md border border-input bg-background px-2 text-sm">
              <option value="ativo">Ativo</option>
              <option value="suspenso">Suspenso</option>
              <option value="inadimplente">Inadimplente</option>
            </select>
          </Field>
          <Field label="Limite de dispositivos"><Input type="number" min={0} value={deviceLimit} onChange={(e) => setDeviceLimit(e.target.value)} /></Field>
          <Field label="Limite de usuários"><Input type="number" min={0} value={userLimit} onChange={(e) => setUserLimit(e.target.value)} /></Field>
        </div>
        <p className="text-[11px] text-muted-foreground">0 = ilimitado. Os limites nativos do Traccar continuam valendo no usuário vinculado.</p>

        <fieldset className="space-y-2 rounded-md border border-border p-3">
          <legend className="px-1 text-xs font-bold">Endereço</legend>
          <Field label="Endereço"><Input value={cad.endereco} onChange={(e) => setC('endereco', e.target.value)} placeholder="Logradouro, nº, bairro" /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Cidade"><Input value={cad.cidade} onChange={(e) => setC('cidade', e.target.value)} /></Field>
            <Field label="UF"><Input value={cad.uf} maxLength={2} onChange={(e) => setC('uf', e.target.value.toUpperCase())} placeholder="SP" /></Field>
            <Field label="País"><Input value={cad.pais} onChange={(e) => setC('pais', e.target.value)} placeholder="Brasil" /></Field>
            <Field label="CEP"><Input value={cad.cep} onChange={(e) => setC('cep', e.target.value)} placeholder="00000-000" /></Field>
          </div>
        </fieldset>

        <fieldset className="space-y-2 rounded-md border border-border p-3">
          <legend className="px-1 text-xs font-bold">Dados</legend>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Cód. Interno"><Input value={cad.cod_interno} onChange={(e) => setC('cod_interno', e.target.value)} placeholder="Código do cliente" /></Field>
            <Field label="Documento"><Input value={cad.documento} onChange={(e) => setC('documento', e.target.value)} placeholder="RG / IE" /></Field>
            <Field label="CPF/CNPJ"><Input value={cad.cpf_cnpj} onChange={(e) => setC('cpf_cnpj', e.target.value)} placeholder="00.000.000/0000-00" /></Field>
            <Field label="Responsável"><Input value={cad.responsavel} onChange={(e) => setC('responsavel', e.target.value)} placeholder="Pessoa de contato" /></Field>
            <Field label="E-mail"><Input type="email" value={cad.email} onChange={(e) => setC('email', e.target.value)} placeholder="contato@cliente.com" /></Field>
            <Field label="Tel. Contato"><Input value={cad.telefone} onChange={(e) => setC('telefone', e.target.value)} placeholder="(11) 9...." /></Field>
            <Field label="Tel. Urgência"><Input value={cad.telefone_urgencia} onChange={(e) => setC('telefone_urgencia', e.target.value)} placeholder="(11) 9...." /></Field>
          </div>
        </fieldset>

        <fieldset className="space-y-2 rounded-md border border-border p-3">
          <legend className="px-1 text-xs font-bold">Financeiro</legend>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Valor do Plano"><Input value={cad.valor_plano} onChange={(e) => setC('valor_plano', e.target.value)} placeholder="R$ 0,00" /></Field>
            <Field label="Tipo de Cobrança">
              <select value={cad.tipo_cobranca} onChange={(e) => setC('tipo_cobranca', e.target.value)} className="h-10 w-full rounded-md border border-input bg-background px-2 text-sm">
                <option value="">—</option>
                <option value="boleto">Boleto</option>
                <option value="pix">PIX</option>
                <option value="cartao">Cartão</option>
                <option value="transferencia">Transferência</option>
                <option value="dinheiro">Dinheiro</option>
              </select>
            </Field>
            <Field label="Data Vencimento"><Input value={cad.data_vencimento} onChange={(e) => setC('data_vencimento', e.target.value)} placeholder="dia 10 ou 2026-01-10" /></Field>
          </div>
          <Field label="Observações Financeira"><Input value={cad.obs_financeira} onChange={(e) => setC('obs_financeira', e.target.value)} placeholder="Notas financeiras" /></Field>
          <Field label="Observações gerais"><Input value={cad.obs} onChange={(e) => setC('obs', e.target.value)} placeholder="Notas internas" /></Field>
        </fieldset>

        {isEdit && <ApplyPlanRow tenantId={tenant!.id} onApplied={onSaved} />}
        </CardContent>
      </Card>

      <div className="flex items-center justify-end gap-2">
        <Button variant="outline" onClick={onClose}>Cancelar</Button>
        <Button onClick={() => save.mutate()} disabled={!valid || save.isPending}>{save.isPending ? 'Salvando…' : 'Salvar'}</Button>
      </div>
    </div>
  );
}

function ApplyPlanRow({ tenantId, onApplied }: { tenantId: number; onApplied: () => void }) {
  const plansQ = useQuery({ queryKey: ['admin', 'plans'], queryFn: () => plansApi.list() });
  const [planId, setPlanId] = useState<number | ''>('');
  const apply = useMutation({
    mutationFn: () => adminApi.applyPlan(tenantId, Number(planId)),
    onSuccess: onApplied,
  });
  const plans = plansQ.data ?? [];
  if (plans.length === 0) return null;
  return (
    <div className="rounded-lg border border-dashed border-border p-3">
      <p className="mb-1 text-xs font-semibold">Aplicar plano (substitui os módulos/cotas do tenant)</p>
      {apply.error && <p className="mb-1 text-xs text-destructive">{(apply.error as Error).message}</p>}
      <div className="flex gap-2">
        <select value={planId} onChange={(e) => setPlanId(e.target.value ? Number(e.target.value) : '')} className="h-9 flex-1 rounded-md border border-input bg-background px-2 text-sm">
          <option value="">— escolher plano —</option>
          {plans.map((p) => <option key={p.id} value={p.id}>{p.nome} ({p.modulos_on} módulos)</option>)}
        </select>
        <Button type="button" variant="outline" disabled={planId === '' || apply.isPending} onClick={() => apply.mutate()}>
          {apply.isPending ? 'Aplicando…' : apply.isSuccess ? 'Aplicado ✓' : 'Aplicar'}
        </Button>
      </div>
    </div>
  );
}

function ModulesModal({ tenant, onClose, onSaved }: { tenant: TenantSummary; onClose: () => void; onSaved: () => void }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['admin', 'modules', tenant.id], queryFn: () => adminApi.getModules(tenant.id) });
  const save = useMutation({
    mutationFn: (mods: { key: string; habilitado: boolean; cota: number | null }[]) => adminApi.setModules(tenant.id, mods),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['admin', 'modules', tenant.id] }); onSaved(); onClose(); },
  });
  return (
    <Modal title={`Módulos — ${tenant.nome_exibicao || tenant.nome}`} onClose={onClose}>
      {q.isLoading ? <p className="text-sm text-muted-foreground">Carregando…</p> : q.data ? (
        <ModuleEditor modules={q.data} saving={save.isPending} onSave={(d) => save.mutate(d)} />
      ) : <p className="text-sm text-destructive">Erro ao carregar.</p>}
    </Modal>
  );
}

function BrandingModal({ tenant, onClose }: { tenant: TenantSummary; onClose: () => void }) {
  const q = useQuery({ queryKey: ['admin', 'branding', tenant.id], queryFn: () => adminApi.getBranding(tenant.id) });
  return (
    <Modal title={`White-label — ${tenant.nome}`} onClose={onClose}>
      {q.isLoading ? <p className="text-sm text-muted-foreground">Carregando…</p> : q.data ? (
        <BrandingForm tenantId={tenant.id} initial={q.data} onClose={onClose} />
      ) : <p className="text-sm text-destructive">Erro ao carregar.</p>}
    </Modal>
  );
}

function BrandingForm({ tenantId, initial, onClose }: { tenantId: number; initial: Branding; onClose: () => void }) {
  const [f, setF] = useState<Branding>(initial);
  const set = (k: keyof Branding, v: string) => setF((p) => ({ ...p, [k]: v || null }));
  const save = useMutation({
    mutationFn: () => adminApi.setBranding(tenantId, f),
    onSuccess: onClose,
  });
  return (
    <div className="space-y-3">
      {save.error && <p className="text-xs text-destructive">{(save.error as Error).message}</p>}
      <Field label="Nome de exibição"><Input value={f.nome_exibicao ?? ''} onChange={(e) => set('nome_exibicao', e.target.value)} placeholder="NautMax" /></Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Cor da marca">
          <div className="flex gap-2">
            <input type="color" value={f.cor_marca ?? '#0B3A5B'} onChange={(e) => set('cor_marca', e.target.value)} className="h-10 w-12 rounded border border-input bg-background" />
            <Input value={f.cor_marca ?? ''} onChange={(e) => set('cor_marca', e.target.value)} placeholder="#0B3A5B" />
          </div>
        </Field>
        <Field label="Cor primária">
          <div className="flex gap-2">
            <input type="color" value={f.cor_primaria ?? '#007AFF'} onChange={(e) => set('cor_primaria', e.target.value)} className="h-10 w-12 rounded border border-input bg-background" />
            <Input value={f.cor_primaria ?? ''} onChange={(e) => set('cor_primaria', e.target.value)} placeholder="#007AFF" />
          </div>
        </Field>
      </div>
      <Field label="Logo (URL)"><Input value={f.logo_url ?? ''} onChange={(e) => set('logo_url', e.target.value)} placeholder="https://…/logo.png" /></Field>
      <Field label="Favicon (URL)"><Input value={f.favicon_url ?? ''} onChange={(e) => set('favicon_url', e.target.value)} placeholder="https://…/favicon.png" /></Field>
      <Field label="Subdomínio (white-label)"><Input value={f.subdominio ?? ''} onChange={(e) => set('subdominio', e.target.value)} placeholder="ex.: revendax" /></Field>
      <Field label="Contato de suporte"><Input value={f.suporte_contato ?? ''} onChange={(e) => set('suporte_contato', e.target.value)} placeholder="e-mail ou telefone" /></Field>
      <div className="flex justify-end gap-2 pt-1">
        <Button variant="outline" onClick={onClose}>Cancelar</Button>
        <Button onClick={() => save.mutate()} disabled={save.isPending}>{save.isPending ? 'Salvando…' : 'Salvar white-label'}</Button>
      </div>
    </div>
  );
}

const ACAO_LABEL: Record<string, string> = {
  'tenant.create': 'Tenant criado',
  'tenant.update': 'Tenant atualizado',
  'tenant.delete': 'Tenant excluído',
  'modules.set': 'Módulos/cotas alterados',
  'modules.set.revenda': 'Módulos alterados (revenda)',
  'branding.set': 'White-label alterado',
  'plan.apply': 'Plano aplicado',
};

function AuditModal({ tenant, onClose }: { tenant: TenantSummary; onClose: () => void }) {
  const q = useQuery({ queryKey: ['admin', 'audit', tenant.id], queryFn: () => adminApi.audit(tenant.id) });
  return (
    <Modal title={`Histórico — ${tenant.nome_exibicao || tenant.nome}`} onClose={onClose}>
      {q.isLoading ? (
        <p className="text-sm text-muted-foreground">Carregando…</p>
      ) : !q.data || q.data.length === 0 ? (
        <p className="text-sm text-muted-foreground">Sem alterações registradas.</p>
      ) : (
        <ul className="space-y-2">
          {q.data.map((e) => (
            <li key={e.id} className="rounded-lg border border-border p-2.5 text-xs">
              <div className="flex items-center justify-between gap-2">
                <span className="font-semibold">{ACAO_LABEL[e.acao] ?? e.acao}</span>
                <span className="text-muted-foreground">{e.em ? formatDateTime(e.em) : ''}</span>
              </div>
              <p className="text-muted-foreground">por {e.ator ?? '—'}</p>
              {e.detalhe && Object.keys(e.detalhe).length > 0 && (
                <pre className="mt-1 overflow-x-auto rounded bg-secondary/60 p-1.5 text-[10px] leading-tight">{JSON.stringify(e.detalhe)}</pre>
              )}
            </li>
          ))}
        </ul>
      )}
    </Modal>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block space-y-1">
      <span className="text-xs font-semibold text-muted-foreground">{label}</span>
      {children}
    </label>
  );
}
